import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { t } from 'i18next';

interface TreadAgentLoaderProps {
  size?: number;
}

// Brand "pyramid" loader: dots cascade down from the peak in timed bands over a faint
// static grid. Ids/classes are prefixed to avoid colliding with app-global styles.
export default function TreadAgentLoader({ size = 40 }: TreadAgentLoaderProps) {
  const theme = useTheme();

  return (
    <Box
      aria-label={t('common.loading')}
      role="status"
      sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center' }}
    >
      <svg aria-hidden height={size} viewBox="0 0 56 56" width={size}>
        <defs>
          <circle id="tal-bg" r="2.4" fill={theme.palette.text.primary} opacity="0.1" />
          <circle id="tal-dot" r="3.1" />
        </defs>
        <style>{`
          .tal{fill:${theme.palette.text.primary};opacity:0;animation:tal-pulse 1000ms cubic-bezier(0.25,1,0.5,1) infinite both;}
          @keyframes tal-pulse{0%{opacity:0;}12%{opacity:1;}55%{opacity:0.85;}100%{opacity:0;}}
          @media (prefers-reduced-motion:reduce){.tal{animation:none;opacity:0.45;}}
          .tal-d00{animation-delay:0ms;}
          .tal-d10,.tal-d11{animation-delay:120ms;}
          .tal-d20,.tal-d21,.tal-d22{animation-delay:240ms;}
          .tal-d30,.tal-d31{animation-delay:360ms;}
          .tal-d40,.tal-d41{animation-delay:480ms;}
        `}</style>

        <use href="#tal-bg" x="6" y="6" />
        <use href="#tal-bg" x="17" y="6" />
        <use href="#tal-bg" x="28" y="6" />
        <use href="#tal-bg" x="39" y="6" />
        <use href="#tal-bg" x="50" y="6" />
        <use href="#tal-bg" x="6" y="17" />
        <use href="#tal-bg" x="17" y="17" />
        <use href="#tal-bg" x="28" y="17" />
        <use href="#tal-bg" x="39" y="17" />
        <use href="#tal-bg" x="50" y="17" />
        <use href="#tal-bg" x="6" y="28" />
        <use href="#tal-bg" x="17" y="28" />
        <use href="#tal-bg" x="28" y="28" />
        <use href="#tal-bg" x="39" y="28" />
        <use href="#tal-bg" x="50" y="28" />
        <use href="#tal-bg" x="6" y="39" />
        <use href="#tal-bg" x="17" y="39" />
        <use href="#tal-bg" x="28" y="39" />
        <use href="#tal-bg" x="39" y="39" />
        <use href="#tal-bg" x="50" y="39" />
        <use href="#tal-bg" x="6" y="50" />
        <use href="#tal-bg" x="17" y="50" />
        <use href="#tal-bg" x="28" y="50" />
        <use href="#tal-bg" x="39" y="50" />
        <use href="#tal-bg" x="50" y="50" />

        <use className="tal tal-d00" href="#tal-dot" x="28" y="6" />
        <use className="tal tal-d10" href="#tal-dot" x="17" y="17" />
        <use className="tal tal-d11" href="#tal-dot" x="39" y="17" />
        <use className="tal tal-d20" href="#tal-dot" x="6" y="28" />
        <use className="tal tal-d21" href="#tal-dot" x="28" y="28" />
        <use className="tal tal-d22" href="#tal-dot" x="50" y="28" />
        <use className="tal tal-d30" href="#tal-dot" x="17" y="39" />
        <use className="tal tal-d31" href="#tal-dot" x="39" y="39" />
        <use className="tal tal-d40" href="#tal-dot" x="6" y="50" />
        <use className="tal tal-d41" href="#tal-dot" x="50" y="50" />
      </svg>
    </Box>
  );
}
